import type { AccessQuery, PermissionMap } from "./types";

type NormalizedPermissions = Map<string, Map<string, Set<string>>>;

/**
 * Permission maps are normalized into Map/Set lookups exactly once per
 * permissions object (the object in context state is referentially stable),
 * so evaluating a query is O(entityIds × actions) set lookups instead of
 * nested array scans on every render.
 */
const normalizedCache = new WeakMap<PermissionMap, NormalizedPermissions>();

function normalize(permissions: PermissionMap): NormalizedPermissions {
  const cached = normalizedCache.get(permissions);
  if (cached) return cached;

  const pages: NormalizedPermissions = new Map();
  for (const [page, entities] of Object.entries(permissions ?? {})) {
    const entityMap = new Map<string, Set<string>>();
    for (const [entityId, actions] of Object.entries(entities ?? {})) {
      entityMap.set(entityId, new Set(Array.isArray(actions) ? actions : []));
    }
    pages.set(page, entityMap);
  }
  normalizedCache.set(permissions, pages);
  return pages;
}

/**
 * Pure RBAC evaluation. Same semantics as the legacy checkPermissionDefault
 * in PrivateComponent, minus the mode-bypass short-circuit (mode routing is the
 * facade's job) and minus any JSX — policy answers are booleans.
 */
export function evaluateRbac(
  query: AccessQuery,
  permissions: PermissionMap | null | undefined
): boolean {
  if (!permissions) return false;

  const pagePermissions = normalize(permissions).get(query.page);
  if (!pagePermissions) return false;

  // No entity scoping → page-level access only (legacy behavior: actions are
  // ignored unless a sourceEntityId is provided).
  if (!query.entityIds) return true;

  const entityIds =
    typeof query.entityIds === "string" ? [query.entityIds] : query.entityIds;
  const actions = query.actions ?? [];

  if (entityIds.length === 0 && actions.length === 0) return true;

  const hasEntityAccess = entityIds.some((id) => pagePermissions.has(id));
  const actionAllowed = (action: string): boolean =>
    entityIds.some((id) => pagePermissions.get(id)?.has(action) === true);

  if (query.forbidWhenActionsPresent) {
    return hasEntityAccess && !actions.some(actionAllowed);
  }

  if (!hasEntityAccess || actions.length === 0) return hasEntityAccess;

  return query.some
    ? actions.some(actionAllowed)
    : actions.every(actionAllowed);
}
